import React from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Container,
  Divider,
  alpha,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { motion } from 'motion/react';
import { Link as RouterLink, useParams } from 'react-router';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import TimerIcon from '@mui/icons-material/Timer';

import bmwDetailImage from 'figma:asset/d30ec8207a5d83317439cd1576ab790533ca9acf.webp';
import gtrInteriorImage from 'figma:asset/50be18a9b4d3d41b72e353b6d0894e636b25bff1.webp';
import amgDetailImage from 'figma:asset/09433cfdb6cef19df4a23efcf57e4bae5be903d8.webp';
import highPileRugImage from 'figma:asset/d960ea2ae017c9c517fff20cc02106b3c07d8387.webp';
import carpetCleaningImage from 'figma:asset/78a4639ad94e671ed42eefd1d3f386ab3118cd06.webp';
import tileStoneImage from 'figma:asset/0469592e0f23d0e2da5ee8b6b5787ba05f83e37d.webp';
import newStoneImage from 'figma:asset/3aa17a1b24c48e260e8b003b56475acb996d5b31.webp';
import yachtDeckImage from 'figma:asset/0eddc9547924722edb4bd32f9660c5b9ce9856e7.webp';
import yachtProfileImage from 'figma:asset/e42e05614c1aac65f21e8377b92681ba073e1674.webp';
import { NotFound } from './NotFound';

const gold = '#C0202A';
const silver = '#C0C0C0';

const chatFactors = [
  {
    icon: <WaterDropIcon sx={{ color: gold }} />,
    label: 'Cleaning Agents',
    desc: 'Safe, high-quality detergents designed to facilitate complete emulsification and soil removal.',
  },
  {
    icon: <ThermostatIcon sx={{ color: gold }} />,
    label: 'Heat',
    desc: 'Truck-mounted systems heat water to optimal temperature, maximizing cleaning agent activity.',
  },
  {
    icon: <AutorenewIcon sx={{ color: gold }} />,
    label: 'Agitation',
    desc: 'Targeted agitation loosens embedded soil, crushed fibers, and surface oils for thorough extraction.',
  },
  {
    icon: <TimerIcon sx={{ color: gold }} />,
    label: 'Time',
    desc: 'We allow proper dwell time for agents to fully break down contaminants before extraction.',
  },
];

const servicesData: Record<string, {
  slug: string;
  overline: string;
  title: string;
  subtitle: string;
  heroImage: string;
  intro: string;
  benefits: string[];
  features: { title: string; description: string }[];
  showChat?: boolean;
  mainImage?: string;
  secondImage?: string;
  marineImage?: string;
  marineImage2?: string;
  frequency?: string;
}> = {
  'auto-detailing': {
    slug: 'auto-detailing',
    overline: 'Auto & Marine Detailing',
    title: 'Precision Detailing for Vehicles That Demand Excellence',
    subtitle: 'Full-service luxury detailing — from paint correction to ceramic coating — performed by Tucson\'s most trusted professionals.',
    heroImage: amgDetailImage,
    mainImage: bmwDetailImage,
    secondImage: gtrInteriorImage,
    marineImage: yachtProfileImage,
    marineImage2: yachtDeckImage,
    intro: `Illuminations offers a comprehensive range of luxury auto and marine detailing services for discerning vehicle owners throughout the Tucson area. We come to you, providing the highest quality detailing services in the industry — all work performed by officers and key principals of the company.

Whether it is your daily driver or your most prized possession, we bring unequaled quality and professionalism to every engagement. Through modern cleaning, correction, and restoration techniques, Illuminations Mobile Detail delivers results that simply cannot be matched.`,
    benefits: [
      'Mobile service — we come to your location',
      'Paint correction & advanced machine polishing',
      'Ceramic coating & polymer paint protection',
      'Headlight polishing & restoration',
      'Engine compartment & undercarriage cleaning',
      'Leather deep cleaning, moisturizing & protection',
      'Interior carpet & fabric cleaning',
      'New car prep services',
      'Luxury maintenance programs',
      'Custom auto detail packages',
    ],
    features: [
      {
        title: 'Paint Correction & Machine Polishing',
        description: 'Remove swirl marks, scratches, oxidation, and surface defects through our advanced multi-stage paint correction process, leaving your paint with a flawless, mirror-like finish.',
      },
      {
        title: 'Paint Protection — Coating & Polymer',
        description: 'Seal your investment with industry-leading ceramic coatings or polymer applications that provide long-lasting protection against UV, contaminants, and environmental damage.',
      },
      {
        title: 'Interior Restoration',
        description: 'Full interior detailing including leather deep cleaning and conditioning, carpet and fabric extraction, and meticulous attention to every surface inside your vehicle.',
      },
      {
        title: 'Luxury Maintenance Programs',
        description: 'Protect your investment year-round with our tailored maintenance programs. Scheduled service intervals keep your vehicle in peak condition without the effort.',
      },
    ],
    frequency: 'For optimal results, we recommend a full detail every 3–6 months and a maintenance wash monthly. Paint protection services vary in longevity depending on the product selected.',
  },
  'carpet-cleaning': {
    slug: 'carpet-cleaning',
    overline: 'Carpet Cleaning & Restoration',
    title: 'Deep Clean That Goes Beyond the Surface',
    subtitle: 'IICRC-certified carpet and area rug cleaning for Tucson homes and businesses — delivering spectacular, long-lasting results since 1994.',
    heroImage: carpetCleaningImage,
    mainImage: highPileRugImage,
    intro: `Lindsey's Carpet & Tile Care has been cleaning and rejuvenating carpet and area rugs in Tucson and the surrounding areas since 1994. We are IICRC Certified in carpet cleaning and offer our customers the highest quality services available.

Everyday foot traffic deposits sand, clay, allergens, bacteria, and oils deep within carpet fibers — well beneath what vacuuming can reach. Our powerful truck-mounted system, combined with professional-grade agents and proven technique, extracts contaminants completely, extending the life and beauty of your carpet.`,
    benefits: [
      'IICRC Certified carpet cleaning professionals',
      'State-of-the-art truck-mounted extraction system',
      'Safe, high-quality cleaning agents',
      'Residential and commercial carpet cleaning',
      'Area rug cleaning and rejuvenation',
      'High-traffic area treatment and agitation',
      'Odor removal services',
      'Carpet protection (Scotchgard or equivalent)',
      'Monthly, quarterly, and annual programs available',
    ],
    features: [
      {
        title: 'The C.H.A.T. Method',
        description: 'Our proven process — Cleaning agents, Heat, Agitation, and Time — works together to produce spectacular results that last.',
      },
      {
        title: 'Truck-Mounted Power',
        description: 'Our state-of-the-art truck-mounted system generates the heat and extraction power needed for complete soil and residue removal.',
      },
      {
        title: 'Complete Soil Extraction',
        description: 'Residues left behind attract new soil rapidly. We ensure complete removal, so your carpet stays cleaner, longer.',
      },
      {
        title: 'Odor Elimination',
        description: 'Penetrating odor removal treatments neutralize pet, food, and environmental odors at the source — not just the surface.',
      },
    ],
    showChat: true,
    frequency: 'Carpet manufacturers recommend professional cleaning every 6–18 months depending on foot traffic, pets, and occupants. Most Tucson customers opt for 6–12 month intervals.',
  },
  'tile-grout': {
    slug: 'tile-grout',
    overline: 'Tile Cleaning & Restoration',
    title: 'Restore Your Tile to Its Original Brilliance',
    subtitle: 'IICRC Certified in Stone, Masonry, and Ceramic Tile — serving Tucson with expert cleaning, restoration, and protection services since 1994.',
    heroImage: newStoneImage,
    mainImage: tileStoneImage,
    intro: `Lindsey's Carpet & Tile Care has been cleaning, restoring, and refinishing man-made and natural stone tile throughout Tucson since 1994. Our IICRC Certification in Stone, Masonry, and Ceramic Tile Cleaning positions us to deliver results that no store-bought product or standard cleaning service can achieve.

Tile and grout absorb the same contaminants as carpet — dirt, oil, grease, and bacteria — but are often overlooked until damage is visible. Our truck-mounted system and professional-grade agents penetrate deeply to remove embedded soils and restore your surfaces to their original luster.`,
    benefits: [
      'IICRC Certified in Stone, Masonry & Ceramic Tile',
      'Natural stone: flagstone, limestone, travertine, slate',
      'Man-made tile: ceramic, porcelain, vinyl, VCT',
      'Grout cleaning, sealing & re-grouting',
      'Floor refinishing and restoration',
      'Commercial and residential service',
      'Sealing and re-sealing after cleaning',
      'Pressure washing concrete & walkways',
      'Furniture moving and replacement included',
    ],
    features: [
      {
        title: 'Natural Stone Expertise',
        description: 'Flagstone, travertine, limestone, slate — each stone requires specific knowledge and technique. Our IICRC training ensures the right approach for every surface.',
      },
      {
        title: 'Grout Restoration',
        description: 'Dirty, stained, or damaged grout is revived through deep cleaning and professional sealing, preventing rapid resoiling and extending the life of your tile installation.',
      },
      {
        title: 'VCT & Vinyl Refinishing',
        description: 'Stripping, sealing, and refinishing commercial and residential VCT and vinyl flooring — craftsmanship and expertise that has been unmatched since 1994.',
      },
      {
        title: 'Complete Convenience',
        description: 'We handle the entire procedure — moving furniture, cleaning, sealing, and returning everything to its place. A completely hands-off experience for our clients.',
      },
    ],
    showChat: true,
    frequency: 'Semi-annual cleaning is recommended for commercial spaces. Residentially, frequency depends on foot traffic, pets, and personal preference. We help you determine the right schedule.',
  },
  'upholstery-leather': {
    slug: 'upholstery-leather',
    overline: 'Upholstery & Leather Care',
    title: 'Expert Care for Your Most Prized Furnishings',
    subtitle: 'IICRC-certified cleaning, conditioning, and protection for all upholstery fabrics and leather — bringing fine furnishings back to their original beauty.',
    heroImage: 'https://images.unsplash.com/photo-1763979628017-ea650631b0be?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1600',
    intro: `Your furnishings represent one of the most significant investments you make for your home. Lindsey's Carpet & Tile Care is IICRC Certified and professionally trained to clean, rejuvenate, and care for all types of upholstery, fabric, and leather.

In time, like the carpet in our homes, upholstery and fabrics collect contaminants that affect cleanliness, appearance, and the health of your living environment — and can cause premature wear and staining. Our expert services remove these contaminants and restore your furniture to a more beautiful and healthy state.`,
    benefits: [
      'IICRC Certified upholstery professionals',
      'Natural and synthetic fabric cleaning and restoration',
      'Protected, Aniline, and Nubuck leather care',
      'Leather deep cleaning, moisturizing & conditioning',
      'Fabric protection application',
      'On-site service — no transport necessary',
      'Odor removal from upholstery',
      'Commercial and residential service',
    ],
    features: [
      {
        title: 'Fabric & Upholstery Cleaning',
        description: 'We clean all natural and synthetic fabrics using the safest, most effective methods for each material type — removing allergens, odors, and embedded soils.',
      },
      {
        title: 'Leather Cleaning & Conditioning',
        description: 'All leather types — Protected, Aniline, and Nubuck — require regular professional cleaning and conditioning to prevent drying, cracking, and permanent staining.',
      },
      {
        title: 'Protection Application',
        description: 'Following cleaning, we apply protective treatments that create a barrier against future soiling, extending the life and beauty of your upholstery.',
      },
      {
        title: 'Leather Preservation',
        description: 'Tucson\'s dry climate accelerates oil loss in leather. Our conditioning treatments replenish natural oils, keeping your leather soft, supple, and beautiful.',
      },
    ],
    frequency: 'Upholstery and fabrics should be cleaned every 6–12 months depending on use, pets, and occupants. Leather should be cleaned and conditioned every 6 months — more frequently when exposed to direct sunlight.',
  },
};

export function Services() {
  const { serviceSlug } = useParams();

  const service = serviceSlug ? servicesData[serviceSlug] : null;

  if (serviceSlug && !service) {
    return <NotFound />;
  }

  // Service Overview page
  if (!service) {
    return (
      <Box sx={{ pt: { xs: 18, md: 22 }, background: '#080808', minHeight: '100vh' }}>
        <Container maxWidth="lg" sx={{ pb: { xs: 8, md: 12 } }}>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Typography variant="overline" sx={{ color: gold, display: 'block', mb: 2 }}>
              Full Spectrum Care
            </Typography>
            <Typography variant="h1" sx={{ fontSize: { xs: '2.5rem', md: '4.5rem' }, color: '#F2EDE4', mb: 3 }}>
              Our Services
            </Typography>
            <Typography variant="body1" sx={{ color: alpha('#F2EDE4', 0.6), maxWidth: 600, lineHeight: 2 }}>
              From your automobile to your home's most delicate surfaces, Lindsey Enterprises delivers luxury-grade cleaning, restoration, and protection — with the same uncompromising standard across every service.
            </Typography>
          </motion.div>
        </Container>

        <Box sx={{ borderTop: `1px solid ${alpha(gold, 0.1)}` }}>
          {Object.values(servicesData).map((s, i) => (
            <Box
              key={s.slug}
              sx={{
                borderBottom: `1px solid ${alpha(gold, 0.08)}`,
                background: i % 2 === 0 ? '#080808' : '#0A0A0A',
              }}
            >
              <Container maxWidth="lg">
                <Grid container spacing={0} alignItems="stretch">
                  <Grid
                    size={{ xs: 12, md: 6 }}
                    sx={{ order: { xs: 0, md: i % 2 === 0 ? 0 : 1 } }}
                  >
                    <Box
                      sx={{
                        height: { xs: 280, md: 420 },
                        backgroundImage: `url(${s.heroImage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        filter: 'brightness(0.7)',
                      }}
                    />
                  </Grid>
                  <Grid
                    size={{ xs: 12, md: 6 }}
                    sx={{ order: { xs: 1, md: i % 2 === 0 ? 1 : 0 } }}
                  >
                    <Box
                      sx={{
                        p: { xs: 5, md: 8 },
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography variant="overline" sx={{ color: gold, display: 'block', mb: 2 }}>
                        {s.overline}
                      </Typography>
                      <Typography variant="h3" sx={{ color: '#F2EDE4', mb: 3, fontSize: { xs: '1.6rem', md: '2rem' } }}>
                        {s.title}
                      </Typography>
                      <Typography variant="body1" sx={{ color: alpha('#F2EDE4', 0.6), mb: 4, lineHeight: 1.9 }}>
                        {s.subtitle}
                      </Typography>
                      <Button
                        component={RouterLink}
                        to={`/services/${s.slug}`}
                        variant="outlined"
                        color="primary"
                        endIcon={<ArrowForwardIcon />}
                        sx={{ alignSelf: 'flex-start', py: 1.5, px: 4, fontSize: '0.65rem', letterSpacing: '0.2em' }}
                      >
                        Learn More
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Container>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // Individual Service Page
  return (
    <Box sx={{ background: '#080808', minHeight: '100vh' }}>
      {/* Hero */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '60vh', md: '70vh' },
          display: 'flex',
          alignItems: 'flex-end',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${service.heroImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.5)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(90deg, rgba(4,4,4,0.77) 0%, rgba(4,4,4,0.45) 100%)',
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pb: 8 }}>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <Typography variant="overline" sx={{ color: gold, display: 'block', mb: 2 }}>
              {service.overline}
            </Typography>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2rem', md: '3.8rem' },
                color: '#F2EDE4',
                maxWidth: 800,
                lineHeight: 1.1,
              }}
            >
              {service.title}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Intro */}
      <Box sx={{ py: { xs: 8, md: 12 }, borderBottom: `1px solid ${alpha(gold, 0.08)}` }}>
        <Container maxWidth="lg">
          <Grid container spacing={8}>
            <Grid size={{ xs: 12, md: 7 }}>
              <Typography variant="overline" sx={{ color: gold, display: 'block', mb: 3 }}>
                Overview
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: alpha('#F2EDE4', 0.7),
                  lineHeight: 2,
                  whiteSpace: 'pre-line',
                  fontSize: '1rem',
                }}
              >
                {service.intro}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Box
                sx={{
                  p: 5,
                  border: `1px solid ${alpha(gold, 0.2)}`,
                  background: alpha(gold, 0.03),
                }}
              >
                <Typography variant="overline" sx={{ color: gold, display: 'block', mb: 3 }}>
                  What's Included
                </Typography>
                <List disablePadding>
                  {service.benefits.map((benefit, idx) => (
                    <ListItem key={`${service.slug}-benefit-${idx}`} disablePadding sx={{ mb: 1.5 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <CheckIcon sx={{ color: gold, fontSize: '0.9rem' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={benefit}
                        primaryTypographyProps={{
                          fontFamily: '"Inter", sans-serif',
                          fontSize: '0.83rem',
                          fontWeight: 300,
                          color: alpha('#F2EDE4', 0.75),
                          lineHeight: 1.6,
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Image Panels */}
      {service.mainImage && (
        <Box sx={{ py: { xs: 8, md: 12 }, borderBottom: `1px solid ${alpha(gold, 0.08)}` }}>
          <Container maxWidth="lg">
            <Grid container spacing={1}>
              <Grid size={{ xs: 12, md: service.secondImage ? 8 : 12 }}>
                <Box
                  sx={{
                    height: { xs: 250, md: 500 },
                    backgroundImage: `url(${service.mainImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                />
              </Grid>
              {service.secondImage && (
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box
                    sx={{
                      height: { xs: 200, md: 500 },
                      backgroundImage: `url(${service.secondImage})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  />
                </Grid>
              )}
            </Grid>
            {service.marineImage && (
              <Box sx={{ mt: 1 }}>
                <Grid container spacing={1} alignItems="stretch">
                  <Grid size={{ xs: 12, md: 5 }}>
                    <Box
                      sx={{
                        height: { xs: 'auto', md: '100%' },
                        minHeight: { md: 380 },
                        p: { xs: 4, md: 6 },
                        border: `1px solid ${alpha(gold, 0.15)}`,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        background: alpha(gold, 0.02),
                      }}
                    >
                      <Typography variant="overline" sx={{ color: gold, display: 'block', mb: 2 }}>
                        Marine Detailing
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{ color: '#F2EDE4', mb: 3, fontSize: { xs: '1.3rem', md: '1.6rem' } }}
                      >
                        We Detail More Than Just Automobiles
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: alpha('#F2EDE4', 0.6), lineHeight: 1.9, fontSize: '0.88rem' }}
                      >
                        From sport boats to motor yachts, Illuminations brings the same precision and uncompromising standards to marine vessels. Hull polishing, interior detailing, upholstery care, and full protective treatments — all performed by the same principals who handle your automobile.
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, md: 7 }}>
                    <Grid container spacing={1} sx={{ height: '100%' }}>
                      <Grid size={6}>
                        <Box
                          sx={{
                            height: { xs: 240, md: 380 },
                            background: '#0a0a0a',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            component="img"
                            src={service.marineImage}
                            alt="Motor yacht"
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                              objectPosition: 'center',
                            }}
                          />
                        </Box>
                      </Grid>
                      <Grid size={6}>
                        <Box
                          sx={{
                            height: { xs: 240, md: 380 },
                            background: '#0a0a0a',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            component="img"
                            src={service.marineImage2 ?? service.marineImage}
                            alt="Motor yacht detail"
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                              objectPosition: 'center',
                            }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Container>
        </Box>
      )}

      {/* C.H.A.T. Method */}
      {service.showChat && (
        <Box sx={{ py: { xs: 8, md: 12 }, background: '#0A0A0A', borderBottom: `1px solid ${alpha(gold, 0.08)}` }}>
          <Container maxWidth="lg">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <Box sx={{ mb: 8, textAlign: 'center' }}>
                <Typography variant="overline" sx={{ color: gold, display: 'block', mb: 2 }}>
                  Our Process
                </Typography>
                <Typography variant="h3" sx={{ color: '#F2EDE4', fontSize: { xs: '1.8rem', md: '2.6rem' } }}>
                  The C.H.A.T. Method
                </Typography>
                <Typography variant="body1" sx={{ color: alpha('#F2EDE4', 0.55), mt: 2, maxWidth: 600, mx: 'auto' }}>
                  Four essential factors work in harmony to produce spectacular, long-lasting results.
                </Typography>
              </Box>
            </motion.div>
            <Grid container spacing={3}>
              {chatFactors.map((factor, i) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={factor.label}>
                  <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}>
                    <Box
                      sx={{
                        p: 4,
                        border: `1px solid ${alpha(gold, 0.15)}`,
                        height: '100%',
                        transition: 'all 0.3s',
                        '&:hover': {
                          borderColor: alpha(gold, 0.4),
                          background: alpha(gold, 0.03),
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 50,
                          height: 50,
                          border: `1px solid ${alpha(gold, 0.3)}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 3,
                        }}
                      >
                        {factor.icon}
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: '"Cormorant Garamond", serif',
                          fontSize: '1.4rem',
                          fontWeight: 500,
                          color: gold,
                          mb: 1,
                        }}
                      >
                        {factor.label}
                      </Typography>
                      <Typography variant="body2" sx={{ color: alpha('#F2EDE4', 0.6), lineHeight: 1.85, fontSize: '0.83rem' }}>
                        {factor.desc}
                      </Typography>
                    </Box>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      {/* Features */}
      <Box sx={{ py: { xs: 8, md: 12 }, borderBottom: `1px solid ${alpha(gold, 0.08)}` }}>
        <Container maxWidth="lg">
          <Typography variant="overline" sx={{ color: gold, display: 'block', mb: 2 }}>
            Service Details
          </Typography>
          <Typography variant="h3" sx={{ color: '#F2EDE4', mb: 8, fontSize: { xs: '1.8rem', md: '2.6rem' } }}>
            What Sets Us Apart
          </Typography>
          <Grid container spacing={4}>
            {service.features.map((feature, i) => (
              <Grid size={{ xs: 12, md: 6 }} key={feature.title}>
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}>
                  <Box sx={{ p: 5, border: `1px solid ${alpha(gold, 0.12)}`, height: '100%', transition: 'border-color 0.3s', '&:hover': { borderColor: alpha(gold, 0.3) } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Box sx={{ width: 2, height: 24, background: gold, flexShrink: 0 }} />
                      <Typography variant="h5" sx={{ color: '#F2EDE4', fontSize: '1.1rem' }}>
                        {feature.title}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: alpha('#F2EDE4', 0.6), lineHeight: 1.9, fontSize: '0.88rem' }}>
                      {feature.description}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ py: { xs: 10, md: 14 }, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ color: '#F2EDE4', mb: 3, fontSize: { xs: '1.8rem', md: '2.8rem' } }}>
            Ready to Experience the Difference?
          </Typography>
          <Typography variant="body1" sx={{ color: alpha('#F2EDE4', 0.6), mb: 6, lineHeight: 2 }}>
            Contact us today to schedule your {service.overline.toLowerCase()} service. We serve Tucson and surrounding areas.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              component={RouterLink}
              to="/contact#booking-form"
              variant="contained"
              color="primary"
              size="large"
              sx={{ py: 2, px: 6, fontSize: '0.7rem', letterSpacing: '0.2em' }}
            >
              Schedule Service
            </Button>
            <Button
              component={RouterLink}
              to="/services"
              variant="outlined"
              color="primary"
              size="large"
              sx={{ py: 2, px: 6, fontSize: '0.7rem', letterSpacing: '0.2em' }}
            >
              All Services
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
